import { POSITION_AVAILABILITY_OPTIONS_ACTOR_ID } from '../state-machine/cartMachine';
import type { ActorRefFrom } from 'xstate';
import type { positionAvailabilityWorkflow } from '../state-machine/positionAvailabilityMachine';

import { useCartStateSelector } from './useCartStateSelector';
import { useCartIsBusy } from './useCartIsBusy';
import { type ICartPositionAvailabilityOptionsDtoData } from '../../../dtos/ProdegaApiWebshop-api-typings-generated';

export type CartUserPromptDialogDescriptor =
  | 'POSITION_AVAILABILITY_OPTIONS'
  | 'DELIVERY_DATE_CREATION'
  | 'DELIVERY_DATE_EXPIRED'
  | 'DELIVERY_DATE_CHANGE'
  | 'DELIVERY_DATE_CHANGE_PROCESSING';

export function useCartUserPromptNotification(): {
  userPromptDialogContentDescriptor: CartUserPromptDialogDescriptor | null;
  needsDeliveryDateSelectionForCreation: boolean;
  positionAvailabilityOptions: ICartPositionAvailabilityOptionsDtoData[] | undefined;
} {
  const { cartIsChangingDeliveryDate, cartIsChangingDeliveryDateExpired } = useCartIsBusy();

  const needsDeliveryDateSelectionForCreation = useCartStateSelector<boolean>(state =>
    state?.matches({
      ProcessingPositions: { CreatingNewOrder: 'DeliveryDateSelection' }
    })
  );
  const needsDeliveryDateSelectionAfterExpiration = useCartStateSelector<boolean>(state =>
    state?.matches({
      DeliveryDateExpired: 'UserInputDeliveryDate'
    })
  );
  const needsDeliveryDateSelectionForChange = useCartStateSelector<boolean>(state =>
    state?.matches({
      ChangingDeliveryDateOfCart: 'UserInputDeliveryDate'
    })
  );

  const positionAvailabilityOptions = useCartStateSelector(state => {
    if (state?.children?.[POSITION_AVAILABILITY_OPTIONS_ACTOR_ID]) {
      const snapshot = (
        state.children[POSITION_AVAILABILITY_OPTIONS_ACTOR_ID] as ActorRefFrom<typeof positionAvailabilityWorkflow>
      ).getSnapshot();
      return snapshot.context.positionAvailabilityOptions;
    }
    return undefined;
  });

  let userPromptDialogContentDescriptor: CartUserPromptDialogDescriptor | null = null;

  if (positionAvailabilityOptions && positionAvailabilityOptions.length > 0) {
    userPromptDialogContentDescriptor = 'POSITION_AVAILABILITY_OPTIONS';
  } else if (needsDeliveryDateSelectionForCreation) {
    userPromptDialogContentDescriptor = 'DELIVERY_DATE_CREATION';
  } else if (needsDeliveryDateSelectionAfterExpiration || cartIsChangingDeliveryDateExpired) {
    userPromptDialogContentDescriptor = 'DELIVERY_DATE_EXPIRED';
  } else if (cartIsChangingDeliveryDate) {
    userPromptDialogContentDescriptor = 'DELIVERY_DATE_CHANGE_PROCESSING';
  } else if (needsDeliveryDateSelectionForChange) {
    userPromptDialogContentDescriptor = 'DELIVERY_DATE_CHANGE';
  }

  return { userPromptDialogContentDescriptor, needsDeliveryDateSelectionForCreation, positionAvailabilityOptions };
}
